import React, { useEffect } from 'react';
import { Drawer, Form, Input, Button, Space } from 'antd';
import { PhoneOutlined, MessageOutlined } from '@ant-design/icons';
import { Customer } from '../../services/customerService';

const { TextArea } = Input;

interface CustomerModalMobileProps {
  visible: boolean;
  onCancel: () => void;
  onSubmit: (data: any) => void;
  editingCustomer: Customer | null;
  loading?: boolean;
}

const CustomerModalMobile: React.FC<CustomerModalMobileProps> = ({
  visible,
  onCancel,
  onSubmit,
  editingCustomer,
  loading = false
}) => {
  const [form] = Form.useForm();

  useEffect(() => {
    if (visible) {
      if (editingCustomer) {
        form.setFieldsValue({
          code: editingCustomer.code,
          name: editingCustomer.name,
          phone: editingCustomer.phone,
          email: editingCustomer.email,
          address: editingCustomer.address,
          tax_code: editingCustomer.tax_code,
          contact_person: editingCustomer.contact_person,
          notes: editingCustomer.notes,
          status: editingCustomer.status
        });
      } else {
        form.resetFields();
      }
    }
  }, [visible, editingCustomer, form]);

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      onSubmit(values);
    } catch (error) {
      // Error handled by antd
    }
  };

  const handleCallPhone = (phoneNumber: string) => {
    if (phoneNumber) {
      // Loại bỏ tất cả ký tự không phải số
      const cleanPhone = phoneNumber.replace(/[^0-9+]/g, '');
      if (cleanPhone) {
        window.open(`tel:${cleanPhone}`, '_self');
      }
    }
  };

  const handleFindZalo = (phoneNumber: string) => {
    if (phoneNumber) {
      // Loại bỏ tất cả ký tự không phải số
      const cleanPhone = phoneNumber.replace(/[^0-9]/g, '');
      if (cleanPhone) {
        // Mở Zalo với số điện thoại
        window.open(`https://zalo.me/${cleanPhone}`, '_blank');
      }
    }
  };

  return (
    <Drawer
      title={editingCustomer ? 'Sửa khách hàng' : 'Thêm khách hàng mới'}
      open={visible}
      onClose={onCancel}
      placement="bottom"
      height="90vh"
      bodyStyle={{ padding: 16, background: '#f5f6fa' }}
      destroyOnClose
      className="customer-mobile-modal"
      footer={null}
    >
      <Form
        form={form}
        layout="vertical"
        onFinish={handleSubmit}
        style={{ width: '100%' }}
      >
        <Form.Item
          name="code"
          label="Mã khách hàng"
          rules={[
            { required: true, message: 'Vui lòng nhập mã khách hàng' },
            { max: 20, message: 'Mã khách hàng không được quá 20 ký tự' }
          ]}
        >
          <Input placeholder="Nhập mã khách hàng" />
        </Form.Item>
        <Form.Item
          name="name"
          label="Tên khách hàng"
          rules={[
            { required: true, message: 'Vui lòng nhập tên khách hàng' },
            { max: 100, message: 'Tên khách hàng không được quá 100 ký tự' }
          ]}
        >
          <Input placeholder="Nhập tên khách hàng" />
        </Form.Item>
        <Form.Item
          name="phone"
          label="Số điện thoại"
          rules={[
            { pattern: /^[0-9+\-\s()]*$/, message: 'Số điện thoại không hợp lệ' }
          ]}
        >
          <Input 
            placeholder="Nhập số điện thoại" 
            suffix={
              <div style={{ display: 'flex', gap: 4 }}>
                <MessageOutlined 
                  style={{ 
                    color: '#0068ff', 
                    cursor: 'pointer',
                    fontSize: '16px'
                  }}
                  onClick={() => {
                    const phoneValue = form.getFieldValue('phone');
                    if (phoneValue) {
                      handleFindZalo(phoneValue);
                    }
                  }}
                  title="Tìm Zalo"
                />
                <PhoneOutlined 
                  style={{ 
                    color: '#1890ff', 
                    cursor: 'pointer',
                    fontSize: '16px'
                  }}
                  onClick={() => {
                    const phoneValue = form.getFieldValue('phone');
                    if (phoneValue) {
                      handleCallPhone(phoneValue);
                    }
                  }}
                  title="Bấm để gọi"
                />
              </div>
            }
          />
        </Form.Item>
        <Form.Item
          name="email"
          label="Email"
          rules={[
            { type: 'email', message: 'Email không hợp lệ' }
          ]}
        >
          <Input placeholder="Nhập email" />
        </Form.Item>
        <Form.Item
          name="tax_code"
          label="Mã số thuế"
          rules={[
            { max: 20, message: 'Mã số thuế không được quá 20 ký tự' }
          ]}
        >
          <Input placeholder="Nhập mã số thuế" />
        </Form.Item>
        <Form.Item
          name="contact_person"
          label="Người liên hệ"
          rules={[
            { max: 100, message: 'Tên người liên hệ không được quá 100 ký tự' }
          ]}
        >
          <Input placeholder="Nhập tên người liên hệ" />
        </Form.Item>
        <Form.Item
          name="address"
          label="Địa chỉ"
          rules={[
            { max: 200, message: 'Địa chỉ không được quá 200 ký tự' }
          ]}
        >
          <TextArea rows={2} placeholder="Nhập địa chỉ" />
        </Form.Item>
        <Form.Item
          name="notes"
          label="Ghi chú"
          rules={[
            { max: 500, message: 'Ghi chú không được quá 500 ký tự' }
          ]}
        >
          <TextArea rows={2} placeholder="Nhập ghi chú (nếu có)" />
        </Form.Item>
        {editingCustomer && (
          <Form.Item
            name="status"
            label="Trạng thái"
            initialValue="active"
          >
            <Input.Group compact>
              <Button
                type={form.getFieldValue('status') === 'active' ? 'primary' : 'default'}
                onClick={() => form.setFieldsValue({ status: 'active' })}
                style={{ width: '50%' }}
              >
                Hoạt động
              </Button>
              <Button
                type={form.getFieldValue('status') === 'inactive' ? 'primary' : 'default'}
                onClick={() => form.setFieldsValue({ status: 'inactive' })}
                style={{ width: '50%' }}
              >
                Không hoạt động
              </Button>
            </Input.Group>
          </Form.Item>
        )}
        <Space style={{ width: '100%', justifyContent: 'flex-end' }}>
          <Button onClick={onCancel}>Hủy</Button>
          <Button type="primary" loading={loading} htmlType="submit">
            {editingCustomer ? 'Cập nhật' : 'Tạo khách hàng'}
          </Button>
        </Space>
      </Form>
    </Drawer>
  );
};

export default CustomerModalMobile;
